import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useAppDispatch } from '../store/hooks'
import { adminLogout } from '../store/adminAuthSlice'
import { onSessionExpiry, SessionExpiryEvent } from '../utils/sessionExpiry'
import { useSnackbar } from '../context/SnackbarContext'

/**
 * SessionExpiryHandler
 *
 * Listens for session expiry events from API interceptors and handles
 * automatic logout and redirect to login page for both user and admin sessions.
 *
 * This component should be rendered once at the app root level.
 */
export function SessionExpiryHandler() {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, isAuthenticated } = useAuth()
  const dispatch = useAppDispatch()
  const snackbar = useSnackbar()

  // Track if we've already handled an expiry to prevent multiple redirects
  const handledRef = useRef(false)

  useEffect(() => {
    const unsubscribe = onSessionExpiry((event: SessionExpiryEvent) => {
      // Prevent multiple handlers from firing
      if (handledRef.current) return

      // Check if we're on a login page - don't redirect if already there
      const isOnUserLogin = location.pathname === '/login'
      const isOnAdminLogin = location.pathname === '/admin/login'

      if (event.type === 'user') {
        // Don't handle if we're already logged out or on login page
        if (!isAuthenticated || isOnUserLogin) return

        handledRef.current = true

        // Log out the user
        logout()

        // Show notification
        snackbar.error('Your session has expired. Please log in again.')

        // Redirect to login page
        navigate('/login', {
          replace: true,
          state: { from: location.pathname, sessionExpired: true }
        })

        // Reset the handled flag after a delay to allow new sessions
        setTimeout(() => {
          handledRef.current = false
        }, 1000)
      } else if (event.type === 'admin') {
        // Don't handle if we're already on admin login page
        if (isOnAdminLogin) return

        handledRef.current = true

        // Log out the admin
        dispatch(adminLogout())

        // Show notification
        snackbar.error('Your admin session has expired. Please log in again.')

        // Redirect to admin login page
        navigate('/admin/login', {
          replace: true,
          state: { from: location.pathname, sessionExpired: true }
        })

        // Reset the handled flag after a delay to allow new sessions
        setTimeout(() => {
          handledRef.current = false
        }, 1000)
      }
    })

    return () => {
      unsubscribe()
    }
  }, [navigate, location, logout, dispatch, isAuthenticated, snackbar])

  // This component doesn't render anything
  return null
}
